import {type FC, useCallback, useEffect} from 'react';
import {JOURNEY_EVENT, TAGGING_FIELDS} from '../../../enums/tagging/index.js';
import {runAnalytics} from '../../analytics.js';

type TaggingFieldClickHandlerProps<P> = {
  taggingLabel: string;
  onClick?: (...args: any[]) => void;
  value?: any;
  journeyEvent?: string;
  errorMessage?: string;
  eventAction?: string;
} & P;

/*
  Higher order component which wraps any clickable component such as Buttons, Links and SelectorItem fields.
  Provides the wrapped component with a click handler that triggers the analytics function and proxies
  the onClick function if one is passed as a prop to the component.
  Behaviour is controlled by the journey event and defaults to sending a field update to adobe.
*/

// const withTaggingFieldClickHandler = (Component: any) => {
//   const FieldWithTaggingClickHandler: FC<Props> = ({
//     taggingLabel,
//     onClick,
//     value,
//     ...rest
//   }: Props) => {
//     const handleOnClick = useCallback(
//       (...args: any) => {
//         if (onClick) {
//           onClick(...args);
//         }

//         if (taggingLabel) {
//           runAnalytics((LBGAnalytics) => {
//             LBGAnalytics.events.send({
//               JourneyEvent: JOURNEY_EVENT.INTERNAL_CLICK,
//               EventNarrative: taggingLabel,
//               EventAction: JOURNEY_EVENT.PAGE_SUBMIT,
//               FieldName: taggingLabel,
//               LinkValue: `button/${taggingLabel.toLowerCase()}`,
//             });
//           });
//         }
//       },
//       [onClick, taggingLabel]
//     );

//     return <Component {...rest} onClick={handleOnClick} value={value} />;
//   };

//   return FieldWithTaggingClickHandler;
// };

const withTaggingFieldClickHandler = <P,>(Component: FC<P>) => {
  const FieldWithTaggingClickHandler: FC<TaggingFieldClickHandlerProps<P>> = ({
    taggingLabel,
    journeyEvent,
    eventAction,
    onClick,
    value,
    errorMessage,
    ...rest
  }: TaggingFieldClickHandlerProps<P>) => {
    const handleOnClick = useCallback(
      (...args: any) => {
        if (onClick) {
          onClick(...args);
        }
        if (taggingLabel) {
          switch (journeyEvent as any) {
            case JOURNEY_EVENT.EXTERNAL_CLICK:
              runAnalytics((LBGAnalytics) => {
                LBGAnalytics.events.send({
                  JourneyEvent: JOURNEY_EVENT.EXTERNAL_CLICK,
                  EventAction: JOURNEY_EVENT.BUTTON_CLICK,
                  EventNarrative: taggingLabel,
                  FieldName: taggingLabel,
                  LinkValue: `button/${taggingLabel.toLocaleLowerCase()}`,
                });
              });
              break;
            case JOURNEY_EVENT.INTERNAL_CLICK:
              runAnalytics((LBGAnalytics) => {
                LBGAnalytics.events.send({
                  JourneyEvent: JOURNEY_EVENT.INTERNAL_CLICK,
                  ErrorMessage: errorMessage,
                  JourneyAction: errorMessage
                    ? TAGGING_FIELDS.VALIDATION_ERROR
                    : undefined,
                  JourneyActionNarrative: errorMessage,
                  EventAction: eventAction,
                  EventNarrative: taggingLabel,
                  FieldName: taggingLabel,
                  LinkValue: `button/${taggingLabel
                    .replace(/[ ,]/g, '')
                    .toLocaleLowerCase()}`,
                });
              });
              break;
            case JOURNEY_EVENT.OVERLAY:
              runAnalytics((LBGAnalytics) => {
                LBGAnalytics.events.send({
                  JourneyEvent: JOURNEY_EVENT.OVERLAY,
                  EventAction: eventAction,
                  EventNarrative: taggingLabel,
                  FieldName: taggingLabel,
                  LinkValue: `button/${taggingLabel.toLocaleLowerCase()}`,
                });
              });
              break;
          }
        }
      },
      [onClick, taggingLabel, journeyEvent, eventAction, errorMessage]
    );

    useEffect(() => {
      if (
        taggingLabel === TAGGING_FIELDS.NEXT &&
        journeyEvent === JOURNEY_EVENT.INTERNAL_CLICK &&
        errorMessage
      ) {
        runAnalytics((LBGAnalytics) => {
          LBGAnalytics.events.send({
            JourneyEvent: JOURNEY_EVENT.INTERNAL_CLICK,
            ErrorMessage: errorMessage,
            JourneyAction: errorMessage
              ? TAGGING_FIELDS.VALIDATION_ERROR
              : undefined,
            JourneyActionNarrative: errorMessage,
            EventAction: eventAction,
            EventNarrative: taggingLabel,
            FieldName: taggingLabel,
            LinkValue: `button/${taggingLabel.toLocaleLowerCase()}`,
          });
        });
      }
    }, [onClick, taggingLabel, journeyEvent, eventAction, errorMessage]);

    return <Component {...(rest as P)} value={value} onClick={handleOnClick} />;
  };

  return FieldWithTaggingClickHandler;
};

export default withTaggingFieldClickHandler;
